import * as React from 'react'
import { useRef } from 'react'

import { Button, Title } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  EmployeeBlocksEmployeeFragment,
  EmployeeBlocksEmployeeFragmentDoc,
  EmployeeBlocksUpdatePayrollInformationDocument,
  ResourceFieldDataFragment,
  ValueDataFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Text from 'modules/core/components/Inputs/Text'
import PanesPadForm from 'modules/core/components/PanesPadForm'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import CustomFieldsForm from 'modules/custom_resources/components/CustomFields/CustomFieldsForm'
import { CompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'
import Codes from 'modules/payroll_integrations/components/Employee/Codes'
import integrationsDescriptor from 'modules/payroll_integrations/components/IntegrationsDescriptor'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  readonly: boolean
  customResourceFieldsData: ResourceFieldDataFragment[]
  customValuesData: ValueDataFragment[]
  employeeCanEditPersonalInfo: boolean
}

class PayrollForm extends Form {
  constructor(values: Record<string, unknown>) {
    super(values, {
      irpf_in_cents: 'cents',
    })
  }
}

const PayrollBlock = ({
  employee,
  readonly,
  customResourceFieldsData,
  customValuesData,
  employeeCanEditPersonalInfo,
}: Props) => {
  const codeRef = React.useRef<Array<Record<string, any>>>([])
  const payrollRef = useRef(
    new PayrollForm({
      irpf_in_cents: employee.irpfCents,
    })
  )
  const payrollForm = payrollRef.current
  const [dirty, setDirty] = React.useState(false)
  const companyInstallations = useLazyRef(() => new CompanyInstallations()).current
  const { loading: loadingIntegrations } = useAsyncData(() => companyInstallations.fetch(), [])

  const [updatePayrollInformationData] = useMutation(
    EmployeeBlocksUpdatePayrollInformationDocument,
    {
      extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
      update: (cache, result) => {
        const updatedEmployee = result.data?.employeesMutations?.updateEmployeesEmployee?.employee
        if (updatedEmployee) {
          cache.writeFragment({
            id: cache.identify(employee),
            fragment: EmployeeBlocksEmployeeFragmentDoc,
            fragmentName: 'EmployeeBlocksEmployee',
            data: {
              ...employee,
              irpfCents: updatedEmployee.irpfCents,
            },
          })
        }
      },
    }
  )

  useUnsafeEffect(() => {
    codeRef.current = codeRef.current.slice(
      0,
      integrationsDescriptor.enabled(companyInstallations).length
    )
  }, [companyInstallations, loadingIntegrations])

  const onChange = () => {
    const anyDirty = codeRef.current.find((form) => form.isDirty())

    if (!anyDirty && !payrollForm.isDirty) setDirty(false)
    else setDirty(true)
  }

  const onSubmit = async () => {
    codeRef.current.forEach((form) => {
      form.onSubmit()
    })

    if (!payrollForm.isDirty) return

    const result = await updatePayrollInformationData({
      variables: {
        employeeId: employee.id,
        irpf: Math.floor(parseFloat(payrollForm.get('irpf_in_cents').value) * 100),
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        const errorKeys = Object.keys(errors)
        const message =
          errorKeys.length > 0
            ? errorKeys
                .map((key) => {
                  return errors[key]
                })
                .join(', ')
            : i18n.t('profile.employee.payroll_information.update_payroll_information_data_error')
        app.setFlash({
          type: 'wrong',
          message: message,
        })
      }
    })

    if (handled.success) {
      payrollForm.cleanAll()
      onChange()
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.payroll_information.update_payroll_information_data'),
      })

      track(trackingEvents.employee.editEmployeeInformationSubmit, {
        fields_edited: [...formGetDirtyFields(payrollForm)],
        legal_entity_country: employee.legalEntity?.country,
        layout_version: 'v1',
      })
    }

    return handled
  }

  const integrations = integrationsDescriptor.enabled(companyInstallations)
  const hasIntegrations = integrations.length > 0

  if (customResourceFieldsData.length === 0 && !employeeCanEditPersonalInfo && !hasIntegrations) {
    return null
  }

  return (
    <PanesPadForm direction="horizontal" boxed>
      <Title
        variant="500"
        illustration="money-bag"
        title={i18n.t('employee_form.custom_fields.payroll_information.title')}
        description={i18n.t('employee_form.custom_fields.payroll_information.description')}
      />
      <>
        {hasIntegrations && (
          <Codes
            employee={employee}
            readonly={readonly}
            codeRef={codeRef}
            onChange={onChange}
            installedIntegrations={companyInstallations}
          />
        )}
        <CustomFieldsForm
          boxed={false}
          customResourceFieldsData={customResourceFieldsData}
          customValuesData={customValuesData}
          padFormDirection="vertical"
          readonly={readonly}
          valuableId={employee.id}
          valuableType="Employee"
          customFieldsOnly
          currency={employee.legalEntity?.currency}
        />
        {employeeCanEditPersonalInfo && (
          <FormRow>
            <Text
              readonly={readonly}
              label={i18n.t('employee_form.irpf.label')}
              field={payrollForm.get('irpf_in_cents')}
              type="number"
              onChange={onChange}
              onBlur={() => track(trackingEvents.employee.personal.addIrpf)}
            />
          </FormRow>
        )}
        {dirty && (
          <FormRow footer>
            <Button submit onClick={onSubmit}>
              {i18n.t('common.submit')}
            </Button>
          </FormRow>
        )}
      </>
    </PanesPadForm>
  )
}

export default observer(PayrollBlock)
